/**
 * RGB / WW / dimmer Main eligibility from the active light mode — not a SKU list.
 * npm test src/utest/sidepanel/light_priority_layout.test.ts
 */

import { devices_config } from 'src/configs/devices';
import type { Device } from 'src/types/Device';
import type { LightMode } from 'src/types/LightType';
import {
	get_mode,
	isRgbCct,
	isRgbwwPm,
	isXMODDevice,
	is_gen_2,
	lightDevicesWithoutModeSwitch,
	real_id,
} from './interface_helpers';
import { isXT1ComponentDevice } from './xt1_helpers/template_helpers';

export type LightPriorityKind = 'color' | 'dimmer';

export type LightPriorityCtRange = {
	minValue: number;
	maxValue: number;
};

export type LightPriorityCapabilities = {
	eligible: boolean;
	kind: LightPriorityKind;
	showRgb: boolean;
	showCct: boolean;
	showWhite: boolean;
	showModeSwitch: boolean;
	sliderProp: string;
	ctRange: LightPriorityCtRange;
	colorType: LightMode;
	hasEffects: boolean;
};

export type LightPriorityChannel = {
	device: Device;
	channel: number;
	name: string;
};

const DEFAULT_CT_RANGE: LightPriorityCtRange = {
	minValue: 2700,
	maxValue: 6500,
};

const isTruthyFlag = (value: unknown): boolean =>
	value !== undefined && value !== false && value !== null;

const readModeFlags = (
	device: Device | undefined,
	mode: string | undefined,
): {
	color: boolean;
	colorTemp: boolean;
	hasWhite: boolean;
	sliderProp: string;
	ctRange: LightPriorityCtRange;
	hasEffects: boolean;
} => {
	const type = device?.type;
	const config = type ? devices_config?.[type] : undefined;
	const flags = mode ? config?.modes?.[mode] : undefined;
	const colorTemp = flags?.color_temp;
	const ctRange =
		colorTemp && typeof colorTemp === 'object'
			? {
					minValue: Number(colorTemp.minValue) || DEFAULT_CT_RANGE.minValue,
					maxValue: Number(colorTemp.maxValue) || DEFAULT_CT_RANGE.maxValue,
				}
			: DEFAULT_CT_RANGE;
	const effects = flags?.effects;

	return {
		color: isTruthyFlag(flags?.color),
		colorTemp: isTruthyFlag(colorTemp),
		hasWhite: isTruthyFlag(flags?.has_white),
		sliderProp: typeof flags?.slider === 'string' ? flags.slider : '',
		ctRange,
		hasEffects: Array.isArray(effects) && effects.length > 0,
	};
};

const NATIVE_MODULE_LIGHT_KINDS = new Set(['light', 'rgb', 'rgbw', 'cct']);

const liveComponentKind = (device: Device | undefined): string => {
	if (isXT1ComponentDevice(device)) {
		return String(device?.component || '').split(':')[0];
	}
	return device?.mode || '';
};

/** XT1 rows whose `component` is a native light/rgb/rgbw/cct channel, not a virtual template. */
export const isNativeXt1LightComponent = (device: Device | undefined): boolean => {
	if (!device || !isXT1ComponentDevice(device)) return false;
	return NATIVE_MODULE_LIGHT_KINDS.has(liveComponentKind(device));
};

/** XMOD modes that should host the light island instead of the generic slider. */
export const isXmodLightMainMode = (mode: string | null | undefined): boolean =>
	mode === 'light' || mode === 'cct' || mode === 'rgbw';

const emptyCapabilities = (): LightPriorityCapabilities => ({
	eligible: false,
	kind: 'dimmer',
	showRgb: false,
	showCct: false,
	showWhite: false,
	showModeSwitch: false,
	sliderProp: '',
	ctRange: DEFAULT_CT_RANGE,
	colorType: 'color',
	hasEffects: false,
});

export const isRgbwwPmRgbcctProfile = (
	device: Device | undefined,
	mode: string | undefined,
	settings?: Record<string, any>,
): boolean => {
	if (!device?.type || !isRgbwwPm(device.type)) return false;
	return (
		mode === 'rgbcct' ||
		device.mode === 'rgbcct' ||
		device.rgbww_profile === 'rgbcct' ||
		settings?.sys?.device?.profile === 'rgbcct'
	);
};

export const canSoftSwitchRgbWw = (
	device: Device | undefined,
	mode: string | undefined,
	settings?: Record<string, any>,
): boolean => {
	if (!device?.type) return false;
	if (isRgbCct(device.type)) return true;
	if (isRgbwwPmRgbcctProfile(device, mode, settings)) return true;
	if (is_gen_2(device)) return false;
	if (lightDevicesWithoutModeSwitch(device.type)) return false;
	const modes = devices_config?.[device.type]?.modes || {};
	return Boolean(modes.color && modes.white);
};

const flagsModeForDevice = (device: Device, profileMode: string | undefined): string | undefined => {
	const channelMode = device.mode || profileMode;
	if (isRgbwwPm(device.type) && channelMode === 'light') return 'light';
	return profileMode || channelMode;
};

export const resolveLightPriorityCapabilities = (
	device: Device | undefined,
	status?: Record<string, any>,
	settings?: Record<string, any>,
): LightPriorityCapabilities => {
	if (!device?.type) return emptyCapabilities();

	const profile = settings?.sys?.device?.profile;
	const mode =
		(typeof profile === 'string' && profile) || get_mode(device, status, settings) || device.mode;
	const componentKind = liveComponentKind(device);
	const isModuleLight = isXMODDevice(device.type) || isXT1ComponentDevice(device);

	let color = false;
	let colorTemp = false;
	let hasWhite = false;
	let sliderProp = '';
	let ctRange = DEFAULT_CT_RANGE;
	let hasEffects = false;

	if (isModuleLight) {
		color = componentKind === 'rgb' || componentKind === 'rgbw';
		colorTemp = componentKind === 'cct';
		hasWhite = componentKind === 'rgbw';
		sliderProp = color || colorTemp || componentKind === 'light' ? 'brightness' : '';
	} else {
		const flagsMode = flagsModeForDevice(device, mode);
		const configModes = devices_config?.[device.type]?.modes || {};
		const modeForFlags =
			[device.mode, flagsMode, mode].find((item) => item && configModes[item]) || flagsMode;
		const flags = readModeFlags(device, modeForFlags);
		color = flags.color;
		colorTemp = flags.colorTemp;
		hasWhite = flags.hasWhite;
		sliderProp = flags.sliderProp;
		ctRange = flags.ctRange;
		hasEffects = flags.hasEffects;
		if (!color && !colorTemp && !sliderProp && devices_config?.[device.type]?.family === 'light') {
			sliderProp = 'brightness';
		}
	}

	if (isRgbCct(device.type)) {
		color = true;
		colorTemp = true;
		const rgbcctFlags = readModeFlags(device, 'rgbcct');
		ctRange = rgbcctFlags.ctRange;
		if (!sliderProp) sliderProp = rgbcctFlags.sliderProp || 'brightness';
	}

	const showModeSwitch = canSoftSwitchRgbWw(device, mode, settings) && (color || colorTemp);
	const rgbCctMode = settings?.['rgbcct:0']?.mode || status?.['rgbcct:0']?.mode;
	let colorType: LightMode = 'color';
	if (isRgbCct(device.type) || isRgbwwPmRgbcctProfile(device, mode, settings)) {
		colorType =
			rgbCctMode === 'cct' || (!rgbCctMode && (mode === 'cct' || mode === 'white'))
				? 'white'
				: 'color';
	} else if (mode === 'rgb' || mode === 'rgbw' || mode === 'color') {
		colorType = 'color';
	} else if (mode === 'cct' || mode === 'white') {
		colorType = 'white';
	}

	const eligible = color || colorTemp || Boolean(sliderProp);
	const showRgb = eligible && color && (!showModeSwitch || colorType === 'color');
	const showCct = eligible && colorTemp && (!showModeSwitch || colorType === 'white');
	const kind: LightPriorityKind = showRgb || showCct ? 'color' : 'dimmer';

	return {
		eligible,
		kind,
		showRgb,
		showCct,
		showWhite: Boolean(hasWhite && showRgb),
		showModeSwitch,
		sliderProp,
		ctRange,
		colorType,
		hasEffects,
	};
};

export const shouldShowLightPaletteTab = (
	device: Device | undefined,
	status?: Record<string, any>,
	settings?: Record<string, any>,
): boolean => {
	const caps = resolveLightPriorityCapabilities(device, status, settings);
	return (caps.showRgb || caps.showCct) && caps.hasEffects;
};

const isSliderOnlyLight = (
	device: Device | undefined,
	status?: Record<string, any>,
	settings?: Record<string, any>,
): boolean => {
	const caps = resolveLightPriorityCapabilities(device, status, settings);
	return caps.eligible && caps.kind === 'dimmer';
};

const siblingChannelId = (parentId: string, channel: number): string =>
	channel > 0 ? `${parentId}_${channel}` : parentId;

const readChannelCount = (device: Device, settings?: Record<string, any>): number => {
	const fromDevice = Number(device.channels_count);
	if (fromDevice > 1) return fromDevice;
	const mode = settings?.sys?.device?.profile || device.mode;
	const fromConfig = Number(devices_config?.[device.type]?.modes?.[mode]?.channels);
	if (fromConfig > 1) return fromConfig;
	if (fromDevice > 0) return fromDevice;
	if (fromConfig > 0) return fromConfig;
	return 1;
};

const siblingName = (device: Device, channel: number, settings?: Record<string, any>): string => {
	const fromSettings =
		settings?.[`light:${channel}`]?.name ||
		settings?.lights?.[channel]?.name ||
		settings?.[`cct:${channel}`]?.name;
	if (typeof fromSettings === 'string' && fromSettings.trim()) return fromSettings;
	if (typeof device.name === 'string' && device.name && Number(device.channel) === channel) {
		return device.name;
	}
	return '';
};

const buildSiblingDevice = (
	parent: Device,
	channel: number,
	listed: Device | undefined,
	settings?: Record<string, any>,
): Device => {
	if (listed) return listed;
	if (channel === 0) return { ...parent, channel: 0 };
	return {
		...parent,
		id: siblingChannelId(real_id(parent.id), channel),
		channel,
		mode: parent.mode === 'rgbx2light' ? 'light' : parent.mode,
		name: siblingName(parent, channel, settings) || (channel === 0 ? parent.name : ''),
	};
};

export const resolveLightMixerChannels = (
	device: Device | undefined,
	status?: Record<string, any>,
	settings?: Record<string, any>,
	deviceList?: Record<string, Device>,
): LightPriorityChannel[] => {
	if (!device?.type) return [];
	const openedChannel = Number(device.channel) || 0;
	if (openedChannel !== 0) return [];
	if (!isSliderOnlyLight(device, status, settings)) return [];

	const parentId = real_id(device.id);
	const listedByChannel = new Map<number, Device>();
	Object.values(deviceList || {}).forEach((item) => {
		if (!item || item.category !== 'light') return;
		if (real_id(item.id) !== parentId) return;
		listedByChannel.set(Number(item.channel) || 0, item);
	});

	const count = Math.max(readChannelCount(device, settings), listedByChannel.size);
	if (count < 2) return [];

	const channels: LightPriorityChannel[] = [];
	for (let channel = 0; channel < count; channel++) {
		const sibling = buildSiblingDevice(device, channel, listedByChannel.get(channel), settings);
		if (!isSliderOnlyLight(sibling, status, settings)) return [];
		channels.push({
			device: sibling,
			channel,
			name: siblingName(sibling, channel, settings) || sibling.name || '',
		});
	}
	return channels;
};

export type LightPriorityDevForce = 'auto' | 'on' | 'off' | 'offline' | 'calibrating';
export type LightPriorityDevLayout = 'auto' | 'rgbWw' | 'rgb' | 'cct' | 'dimmer';
export type LightPriorityOverlay = 'offline' | 'calibrating';

export type LightPriorityDevOption = { id: string; label: string };

/** Matches `compute-device-state` uiState.type for dimmer calibration in progress. */
export const LIGHT_UI_STATE_CALIBRATING = 4;

const LIGHT_PRIORITY_DEV_FORCES: LightPriorityDevForce[] = [
	'auto',
	'on',
	'off',
	'offline',
	'calibrating',
];
const LIGHT_PRIORITY_DEV_LAYOUTS: LightPriorityDevLayout[] = ['auto', 'rgbWw', 'rgb', 'cct', 'dimmer'];

export const isLightPriorityDevForce = (
	value: string | null | undefined,
): value is LightPriorityDevForce =>
	Boolean(value && LIGHT_PRIORITY_DEV_FORCES.includes(value as LightPriorityDevForce));

export const isLightPriorityDevLayout = (
	value: string | null | undefined,
): value is LightPriorityDevLayout =>
	Boolean(value && LIGHT_PRIORITY_DEV_LAYOUTS.includes(value as LightPriorityDevLayout));

export const LIGHT_PRIORITY_DEV_OPTIONS: LightPriorityDevOption[] = [
	{ id: 'auto', label: 'Auto' },
	{ id: 'on', label: 'On' },
	{ id: 'off', label: 'Off' },
	{ id: 'offline', label: 'Offline' },
	{ id: 'calibrating', label: 'Calibrating' },
];

export const LIGHT_PRIORITY_LAYOUT_DEV_OPTIONS: LightPriorityDevOption[] = [
	{ id: 'auto', label: 'Auto' },
	{ id: 'rgbWw', label: 'RGB+WW' },
	{ id: 'rgb', label: 'RGB' },
	{ id: 'cct', label: 'CCT' },
	{ id: 'dimmer', label: 'Dimmer' },
];

export const LIGHT_OVERLAY_COPY: Record<
	LightPriorityOverlay,
	{
		value: { key: string; default: string };
		label: { key: string; default: string };
		icon: string;
		tone: 'muted';
		accentColor: string;
	}
> = {
	offline: {
		value: { key: 'label_sensor_overlay_offline', default: 'Offline' },
		label: {
			key: 'label_sensor_overlay_offline_hint',
			default: 'Device unavailable',
		},
		icon: 'fa-solid fa-cloud-slash',
		tone: 'muted',
		accentColor: 'var(--color-theme-fg-02)',
	},
	calibrating: {
		value: { key: 'label_light_overlay_calibrating', default: 'Calibrating' },
		label: {
			key: 'dimmer_calibration',
			default: 'Dimmer calibration',
		},
		icon: 'fa-solid fa-sliders',
		tone: 'muted',
		accentColor: 'var(--color-theme-fg-02)',
	},
};

export const applyForcedLightPower = (ison: boolean, force?: LightPriorityDevForce): boolean => {
	if (force === 'on') return true;
	if (force === 'off') return false;
	return ison;
};

export const applyForcedLightCapabilities = (
	caps: LightPriorityCapabilities,
	layout?: LightPriorityDevLayout,
): LightPriorityCapabilities => {
	if (!layout || layout === 'auto' || !caps.eligible) return caps;
	const sliderProp = caps.sliderProp || 'brightness';
	if (layout === 'rgbWw') {
		const colorType = caps.colorType === 'white' ? 'white' : 'color';
		return {
			...caps,
			kind: 'color',
			showRgb: colorType === 'color',
			showCct: colorType === 'white',
			showWhite: false,
			showModeSwitch: true,
			colorType,
			sliderProp,
		};
	}
	if (layout === 'rgb') {
		return {
			...caps,
			kind: 'color',
			showRgb: true,
			showCct: false,
			showWhite: false,
			showModeSwitch: false,
			colorType: 'color',
			sliderProp,
		};
	}
	if (layout === 'cct') {
		return {
			...caps,
			kind: 'color',
			showRgb: false,
			showCct: true,
			showWhite: false,
			showModeSwitch: false,
			colorType: 'white',
			sliderProp,
		};
	}
	return {
		...caps,
		kind: 'dimmer',
		showRgb: false,
		showCct: false,
		showWhite: false,
		showModeSwitch: false,
		sliderProp,
	};
};

export const inferLightOverlay = ({
	online,
	force,
	uiStateType,
}: {
	online?: boolean;
	force?: LightPriorityDevForce;
	uiStateType?: number;
}): LightPriorityOverlay | null => {
	if (force === 'offline') return 'offline';
	if (force === 'calibrating') return 'calibrating';
	if (force === 'on' || force === 'off') return null;
	if (!online) return 'offline';
	if (uiStateType === LIGHT_UI_STATE_CALIBRATING) return 'calibrating';
	return null;
};
